import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "../supabase";
import SemicircleGauge from "../components/SemicircleGauge";
import DonateModal from "../components/DonateModal";
import DonorWall from "../components/DonorWall";
import QRCodeModal from "../components/QRCodeModal";
import defaultAvatar from "../assets/BBall@2x.png";
import Footer from "../components/Footer";

const DEFAULT_SEASON_END = new Date("2026-06-01T00:00:00");

export default function PlayerPage() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [showDonate, setShowDonate] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [donations, setDonations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const [playerRes, donRes, settingsRes] = await Promise.all([
      supabase.from("players").select("*").eq("id", Number(id)).single(),
      supabase
        .from("donations")
        .select("*")
        .eq("player_id", Number(id))
        .order("created_at", { ascending: false }),
      supabase.from("coach_settings").select("*").limit(1).single(),
    ]);

    if (playerRes.data) setPlayer(playerRes.data);
    if (donRes.data) setDonations(donRes.data);
    if (settingsRes.data) setSettings(settingsRes.data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-black/40">Loading...</p>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-black mb-2">
            Player not found
          </h2>
          <Link to="/" className="text-black font-semibold hover:underline">
            Back to team
          </Link>
        </div>
      </div>
    );
  }

  const raised = donations.reduce((sum, d) => sum + Number(d.amount), 0);
  const goal = player.goal_amount;
  const displayMode = settings?.display_mode || "team";
  const isTeamMode = displayMode === "team";
  const shareUrl = window.location.href;
  const photoUrl = player.photo_url || defaultAvatar;

  // Countdown — use end_date from coach_settings if available
  const seasonEnd = settings?.end_date
    ? new Date(settings.end_date + "T23:59:59")
    : DEFAULT_SEASON_END;
  const now = new Date();
  const ended = now >= seasonEnd;
  const daysLeft = Math.max(0, Math.ceil((seasonEnd - now) / (1000 * 60 * 60 * 24)));
  const fundraiserClosed = (settings?.fundraiser_closed || false) || ended;

  const donorWallData = donations.map((d) => ({
    id: d.id,
    name: d.donor_name,
    amount: Number(d.amount),
    message: d.message,
    anonymous: d.is_anonymous,
  }));

  // OG image — use player photo or fallback to logo (needs absolute URL for meta tags)
  const ogImage = player.photo_url || `${window.location.origin}/BBall@2x.png`;

  // Share handlers
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Support ${player.name} — Buchtel Griffins Baseball Program`,
          text: `Help ${player.name} reach their $${goal} fundraising goal!`,
          url: shareUrl,
        });
      } catch (e) {
        // User canceled or share already in progress — ignore
      }
    }
  };

  const handleFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const handleTwitter = () => {
    const text = `Help ${player.name} reach their fundraising goal for Buchtel Griffins Baseball Program!`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const handleSMS = () => {
    const text = `Support ${player.name} on the Buchtel Griffins Baseball Program! Donate here: ${shareUrl}`;
    window.open(`sms:?body=${encodeURIComponent(text)}`);
  };

  return (
    <div className="min-h-screen pb-24 bg-cream">
      <Helmet>
        <title>{`${player.name} #${player.jersey_number} — Buchtel Griffins Baseball Program`}</title>
        <meta property="og:title" content={`Support ${player.name} — Buchtel Griffins Baseball Program`} />
        <meta property="og:description" content={`Help ${player.name} #${player.jersey_number} gear up for the season!`} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Support ${player.name} — Buchtel Griffins Baseball Program`} />
        <meta name="twitter:description" content={`Help ${player.name} #${player.jersey_number} gear up for the season!`} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Countdown banner */}
      {!ended && daysLeft > 0 && (
        <div className="bg-red-600 text-white text-center py-2.5 text-sm font-semibold">
          {daysLeft === 1
            ? "Last day to donate!"
            : `Only ${daysLeft} days left to donate!`}
        </div>
      )}

      {/* Hero */}
      <header className="bg-black text-white">
        <div className="max-w-2xl mx-auto px-5 pt-5 pb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to team
          </Link>

          <div className="text-center">
            {/* Player photo with red ring */}
            <div className="relative w-32 h-32 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-[4px] border-red-600" />
              <img
                src={photoUrl}
                alt={player.name}
                className={`w-full h-full rounded-full ${player.photo_url ? "object-cover" : "object-contain p-3 bg-white/10"}`}
              />
              {/* Jersey number badge */}
              <div className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center border-2 border-black">
                #{player.jersey_number}
              </div>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-1">
              {player.name}
            </h1>
            <p className="text-white/50 text-sm font-medium">
              Buchtel Griffins Baseball Program &middot; Akron, OH
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        {/* Gauge + stats card */}
        <section className="-mt-8 bg-white rounded-[12px] border border-warm-gray shadow-sm px-5 pt-5 pb-4 mb-6">
          {isTeamMode ? (
            <p className="text-center text-navy/70 py-4 text-lg font-semibold">
              Help {player.name} gear up for the season!
            </p>
          ) : (
            <>
              <SemicircleGauge raised={raised} goal={goal} />
              <p className="text-center text-sm text-black/50 mt-1">
                Help {player.name} gear up for the season!
              </p>
            </>
          )}
        </section>

        {/* Donate CTA */}
        <button
          onClick={() => !fundraiserClosed && setShowDonate(true)}
          disabled={fundraiserClosed}
          className={`w-full font-bold py-4 rounded-xl transition-colors text-lg shadow-sm mb-6 ${
            fundraiserClosed
              ? "bg-warm-gray text-navy/40 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }`}
        >
          {ended
            ? "Fundraiser Ended — Thank You!"
            : fundraiserClosed
            ? "Fundraiser Closed — Thank You!"
            : `Donate for ${player.name}`}
        </button>

        {/* Social share row */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-bold text-black mb-3">
            Spread the Word
          </h2>
          <div className="grid grid-cols-5 gap-2">
            {/* Native share */}
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                onClick={handleNativeShare}
                className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
              >
                <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <span className="text-[10px] font-medium text-black/60">Share</span>
              </button>
            )}
            {/* QR Code */}
            <button
              onClick={() => setShowQR(true)}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">QR</span>
            </button>
            {/* Facebook */}
            <button
              onClick={handleFacebook}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">Facebook</span>
            </button>
            {/* X/Twitter */}
            <button
              onClick={handleTwitter}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">Post</span>
            </button>
            {/* SMS */}
            <button
              onClick={handleSMS}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">Text</span>
            </button>
          </div>
        </section>

        {/* Donor Wall */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-bold text-black mb-3">
            {player.name}{player.name.endsWith("s") ? "'" : "'s"} Supporters
            {donorWallData.length > 0 && (
              <span className="text-black/30 font-normal text-sm ml-2">
                ({donorWallData.length})
              </span>
            )}
          </h2>
          <DonorWall donors={donorWallData} />
        </section>

        {/* Premier Sponsor Block */}
        <section className="mb-10">
          <div className="bg-black rounded-xl p-6 text-center">
            <p className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-2">
              Premier Sponsor
            </p>
            <h3 className="font-display text-xl font-bold text-white mb-2">
              Get Your Business Seen by Griffins Families
            </h3>
            <p className="text-white/50 text-sm mb-5 max-w-sm mx-auto">
              Sponsor {player.name}'s page and reach hundreds of local supporters, parents, and community members.
            </p>
            <a
              href={`mailto:jedwards@pspeople.org?subject=Sponsor ${player.name} %23${player.jersey_number}&body=I'm interested in sponsoring ${player.name}'s fundraising page on the Buchtel Griffins Baseball Program donation site.`}
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-colors text-sm"
            >
              Sponsor Now
            </a>
          </div>
        </section>
      </main>

      <Footer />

      {/* Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 p-4 bg-cream/95 backdrop-blur-sm border-t border-warm-gray">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => !fundraiserClosed && setShowDonate(true)}
            disabled={fundraiserClosed}
            className={`w-full font-bold py-4 rounded-xl transition-colors text-lg shadow-sm ${
              fundraiserClosed
                ? "bg-warm-gray text-navy/40 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {ended
            ? "Fundraiser Ended — Thank You!"
            : fundraiserClosed
            ? "Fundraiser Closed — Thank You!"
            : `Donate for ${player.name}`}
          </button>
        </div>
      </div>

      {showDonate && (
        <DonateModal
          playerId={player.id}
          playerName={player.name}
          onClose={() => setShowDonate(false)}
          onDonated={() => { setShowDonate(false); fetchData(); }}
        />
      )}

      {showQR && (
        <QRCodeModal
          url={shareUrl}
          playerName={player.name}
          onClose={() => setShowQR(false)}
        />
      )}
    </div>
  );
}
